import { useId } from 'react'

import { formatAxisValue2 } from '../../lib/format'
import { mapFrame, onMap, type Clip } from '../../lib/stateGeometry'
import { useBoxSize } from './useBoxSize'
import { StateTip } from './StateTip'

/**
 * The prototype's state map (R72): the two-gradient field, the dashed "usual"
 * ring, the axis ends above and below, the four corner words in pills, and one
 * dot per stored reading whose two axes are both present. Every word is the
 * payload's; every position is a stored value on the payload's own clip.
 *
 * A reading with a missing axis has NO dot — never one at zero. When it is the
 * selected one, the map says so in its banner ("10 Sep · no map position").
 */
export interface MapDay {
  id: string
  /** "12 Sep", the dot's label. */
  short: string
  /** "12 September", for the accessible name. */
  long: string
  act: number | null
  bal: number | null
  word: string | null
}

export function StateMap({ days, selectedId, clip, dz, corners, ends, names, locked, noPosition, onSelect }: {
  days: readonly MapDay[]
  selectedId: string
  clip: Clip | null
  dz: number | null
  corners: { tl: string; tr: string; bl: string; br: string } | null
  ends: { pos: string; neg: string } | null
  names: { activation: string; balance: string }
  locked: boolean
  noPosition: string
  onSelect: (id: string) => void
}) {
  const [box, size] = useBoxSize<HTMLDivElement>()
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '')
  const w = size.width
  const h = size.height
  const frame = clip !== null && w !== 0 ? mapFrame(w, h, clip) : null
  const selected = days.find((d) => d.id === selectedId) ?? null
  const unplaced = selected !== null && clip !== null && !onMap(selected.act, selected.bal, clip)

  return <div className="bp-map" ref={box} data-testid="state-map" data-locked={locked ? 'true' : undefined}>
    {frame !== null && <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img"
      aria-label={`State map: ${names.balance.toLowerCase()} on the horizontal axis and ${names.activation.toLowerCase()} on the vertical axis. Use the date buttons to select a reading.`}>
      <defs>
        <linearGradient id={`${uid}top`}><stop offset="0%" className="bp-stop bp-stop--tl" /><stop offset="100%" className="bp-stop bp-stop--tr" /></linearGradient>
        <linearGradient id={`${uid}bottom`}><stop offset="0%" className="bp-stop bp-stop--bl" /><stop offset="100%" className="bp-stop bp-stop--br" /></linearGradient>
        <linearGradient id={`${uid}fade`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="white" stopOpacity="0" /><stop offset="100%" stopColor="white" stopOpacity="1" /></linearGradient>
        <mask id={`${uid}mask`}><rect x={frame.padX} y={frame.padY} width={frame.pw} height={frame.ph} fill={`url(#${uid}fade)`} /></mask>
      </defs>
      <rect x={frame.padX} y={frame.padY} width={frame.pw} height={frame.ph} rx="16" fill={`url(#${uid}top)`} />
      <rect x={frame.padX} y={frame.padY} width={frame.pw} height={frame.ph} rx="16" fill={`url(#${uid}bottom)`} mask={`url(#${uid}mask)`} />
      {dz !== null && <circle className="bp-ring" cx={frame.x(0)} cy={frame.y(0)} r={frame.ring(dz)} data-testid="state-map-ring" />}
      <line className="bp-axisline" x1={frame.padX} y1={frame.y(0)} x2={frame.right} y2={frame.y(0)} />
      <line className="bp-axisline" x1={frame.x(0)} y1={frame.padY} x2={frame.x(0)} y2={frame.bottom} />
      {ends !== null && <>
        <text x={frame.centre} y="17" textAnchor="middle">{`${ends.pos} ↑`}</text>
        <text x={frame.centre} y={frame.footLabelY} textAnchor="middle">{`${ends.neg} ↓`}</text>
      </>}
    </svg>}
    {frame !== null && corners !== null && <>
      <span className="bp-corner" style={{ left: frame.cornerInset, top: frame.cornerTop }}>{corners.tl}</span>
      <span className="bp-corner bp-corner--end bp-corner--tr" style={{ right: frame.cornerInset, top: frame.cornerTop }}>{corners.tr}</span>
      <span className="bp-corner bp-corner--bl" style={{ left: frame.cornerInset, top: frame.cornerFoot }}>{corners.bl}</span>
      <span className="bp-corner bp-corner--end bp-corner--br" style={{ right: frame.cornerInset, top: frame.cornerFoot }}>{corners.br}</span>
    </>}
    {/* After the corners, as in the prototype's SVG, which draws the banner last.
        Hidden like the rest of the map picture, which is one `role="img"`. */}
    {frame !== null && !locked && unplaced && <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true" style={{ pointerEvents: 'none' }}>
      <rect className="bp-banner" x={frame.banner.x} y={frame.banner.y} width={frame.banner.width} height="26" rx="5" />
      <text x={frame.centre} y={frame.banner.textY} textAnchor="middle" data-testid="state-map-banner">{`${selected.short} · ${noPosition}`}</text>
    </svg>}
    {frame !== null && clip !== null && !locked && days.map((d) => {
      if (d.act === null || d.bal === null || !onMap(d.act, d.bal, clip)) return null
      const act = d.act
      const bal = d.bal
      const pressed = d.id === selectedId
      return <StateTip key={d.id} pin={false}
        text={`${d.short} · ${d.word ?? '—'}. ${names.activation} ${formatAxisValue2(act)}, ${names.balance.toLowerCase()} ${formatAxisValue2(bal)}. Select to explore.`}>
        {(tip) => <button type="button" className="bp-mark" {...tip} aria-pressed={pressed}
          aria-label={`${d.long}: ${d.word ?? '—'}`} style={{ left: frame.x(bal), top: frame.y(act) }}
          onClick={() => onSelect(d.id)}>
          {pressed && <span className="bp-dotlabel">{d.short}</span>}
        </button>}
      </StateTip>
    })}
  </div>
}
